export const ROLE_ALIASES = {
  administrator: 'admin',
  administrador: 'admin',
  logistics_operator: 'area_manager',
  'logistics operator': 'area_manager',
  regional_manager: 'regional_manager',
  'regional manager': 'regional_manager',
  'store manager': 'store_manager',
  'area manager': 'area_manager',
};

export const IGNORED_PERMISSIONS = new Set([
  'create_new_user',
  'desactivate_user',
  'change_password',
  'upload_csv',
  'view_storage',
  'view_product',
  'make_request',
  'view_request',
  'answer_request',
  'edit_capacity',
  'manage_request',
]);

export const PERMISSION_MODULES = [
  {
    key: 'stock',
    match: (n) => n.includes('stock') && !n.includes('settings') && !n.includes('movement'),
  },
  { key: 'movements', match: (n) => n.includes('movement') },
  { key: 'processTransfers', match: (n) => n.includes('process_') },
  {
    key: 'shipments',
    match: (n) => n.includes('shipment') || n.includes('received_date'),
  },
  {
    key: 'capacity',
    match: (n) => n.includes('capacity') && !n.includes('settings'),
  },
  { key: 'arrivals', match: (n) => n.includes('arrival') },
  { key: 'orders', match: (n) => n.includes('order') },
  { key: 'pullback', match: (n) => n.includes('pullback') },
  {
    key: 'salesAnalysis',
    match: (n) => n.includes('sales_analysis') || n.includes('sales_cogs') || n.includes('sales_export'),
  },
  {
    key: 'timeToSell',
    match: (n) => n.includes('time_to_sell') || n.includes('product_sales'),
  },
  { key: 'settings', match: (n) => n.includes('settings') },
  { key: 'chat', match: (n) => n.includes('chat') },
  { key: 'support', match: (n) => n.includes('support') },
  { key: 'extras', match: () => true },
];


export function getRoleName(name) {
  const key = String(name || '').trim().toLowerCase();
  return ROLE_ALIASES[key] || key;
}

export function permissionAccessType(name) {
  const n = String(name || '').toLowerCase();
  if (n.startsWith('view_')) return 'read';
  if (n.startsWith('make_') || n.startsWith('edit_')) return 'write';
  return 'other';
}

export function translatePermissionLabel(name, t) {
  const key = String(name || '').trim();
  return t(`settings.rolePolicies.permissions.${key}`, {
    defaultValue: key.replace(/_/g, ' '),
  });
}

export function filterVisiblePermissions(permissions) {
  return (permissions || []).filter(
    (p) => !IGNORED_PERMISSIONS.has(String(p.name || '').trim())
  );
}

function sortPermissionsByAccess(permissions) {
  const order = { read: 0, write: 1, other: 2 };
  return [...permissions].sort((a, b) => {
    const ao = order[permissionAccessType(a.name)] ?? 9;
    const bo = order[permissionAccessType(b.name)] ?? 9;
    if (ao !== bo) return ao - bo;
    return String(a.name).localeCompare(String(b.name));
  });
}

export function permissionModuleKey(name) {
  const n = String(name || '').toLowerCase();
  for (const mod of PERMISSION_MODULES) {
    if (mod.match(n)) return mod.key;
  }
  return 'extras';
}

//agrupar as permissões por módulo (stock,movements,etc.)
export function groupPermissionsByModule(permissions) {
  const buckets = new Map(PERMISSION_MODULES.map((m) => [m.key, []]));

  for (const perm of permissions) {
    const key = permissionModuleKey(perm.name);
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(perm);
  }

  return PERMISSION_MODULES.map((m) => ({
    key: m.key,
    permissions: sortPermissionsByAccess(buckets.get(m.key) || []),
  })).filter((g) => g.permissions.length > 0);
}

export function isAdminRole(name) {
  return getRoleName(name) === 'admin';
}

export function loggedRoleName(role) {
  if (Array.isArray(role)) return getRoleName(role[0]);
  if (role && typeof role === 'object') return getRoleName(role.name);
  return getRoleName(role);
}

const EDITABLE_BY_ROLE = {
  regional_manager: ['area_manager', 'store_manager'],
};

//verificar se o user pode editar o cargo
export function canEditTargetRole(loggedRole, targetRole) {
  const logged = loggedRoleName(loggedRole);
  if (isAdminRole(logged)) return true;
  if (!targetRole) return false;

  const allowed = new Set(
    (EDITABLE_BY_ROLE[logged] || []).map((name) => getRoleName(name))
  );
  return String(targetRole)
    .split(',')
    .map((part) => getRoleName(part))
    .some((target) => target && allowed.has(target));
}

export function canCustomizeUserPermissions(loggedRole, targetRole) {
  if (!targetRole || isAdminRole(targetRole)) return false;
  return canEditTargetRole(loggedRole, targetRole);
}

//verificar se o user tem a permissão 
export function userHasPermission(user, permissionName) {
  if (!user || permissionName == null || permissionName === '') return false;
  if (isAdminRole(loggedRoleName(user.role))) return true;
  const wanted = (Array.isArray(permissionName) ? permissionName : [permissionName])
    .map((n) => String(n || '').trim().toLowerCase())
    .filter(Boolean);
  if (!wanted.length) return false;
  const perms = user.permissions || [];
  return wanted.some((w) =>
    perms.some((name) => String(name || '').trim().toLowerCase() === w)
  );
}

export function userHasPermissionPrefix(user, prefix) {
  if (!user || !prefix) return false;
  if (isAdminRole(loggedRoleName(user.role))) return true;
  const p = String(prefix).trim().toLowerCase();
  return (user.permissions || []).some((name) =>
    String(name || '').trim().toLowerCase().startsWith(p)
  );
}

export function userCanAccessRoute(user, requirement) {
  if (!user) return false;
  if (requirement === null) return true; 
  if (requirement === undefined) return false;
  if (isAdminRole(loggedRoleName(user.role))) return true;
  if (requirement && typeof requirement === 'object' && !Array.isArray(requirement) && requirement.prefix) {
    return userHasPermissionPrefix(user, requirement.prefix);
  }
  return userHasPermission(user, requirement);
}

export function canViewRolePolicies(user) {
  return userHasPermission(user, 'view_settings_role_policies');
}

export function canEditRolePolicies(user) {
  return userHasPermission(user, 'make_settings_role_policies');
}

//listar as permissões de um cargo
export function permissionIdsForRole(roles, matrix, roleName) {
  if (isAdminRole(roleName)) return null;
  const key = getRoleName(roleName);
  const ids = new Set();
  for (const role of roles || []) {
    if (getRoleName(role.name) !== key && String(role.name) !== String(roleName)) {
      continue;
    }
    for (const permId of matrix?.[role.id_role] || []) ids.add(permId);
  }
  return ids;
}
